import { useEffect, useState, useCallback } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import {
  Cpu,
  Loader2,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Play,
  Square,
  Download,
  Trash2,
  AlertTriangle,
  Plus,
  Wrench,
  Bot,
  MessageSquare,
  Zap,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { formatBytes, getEnv } from '@/lib/utils'
import { gatewayFetch } from '@/lib/gateway-auth'
import { useSystemStore, GPUStats } from '@/stores/system'
import { useLlmStore } from '@/stores/llm'

interface ModelCapabilities {
  toolCalling: boolean
  toolParser?: string
  agentic: boolean
}

interface ManagedModel {
  id: string
  name: string
  status: 'running' | 'stopped' | 'downloading' | 'downloaded' | 'error'
  gpu?: number
  port?: number
  pid?: number
  error?: string
  downloadProgress?: number
  startedAt?: string
  capabilities?: ModelCapabilities
  sizeBytes?: number
}

function getGatewayUrl(): string {
  return getEnv('VITE_GATEWAY_URL').replace(/\/$/, '') || '/api'
}

function GPUCard({ gpu }: { gpu: GPUStats }) {
  const usagePercent = gpu.memoryTotal > 0 ? (gpu.memoryUsed / gpu.memoryTotal) * 100 : 0

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium">GPU {gpu.id} - {gpu.name}</CardTitle>
        <Cpu className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">VRAM Usage</span>
            <span>
              {formatBytes(gpu.memoryUsed * 1024 * 1024)} / {formatBytes(gpu.memoryTotal * 1024 * 1024)}
            </span>
          </div>
          <Progress value={usagePercent} className="h-2" />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{gpu.temperature}°C</span>
            <span>{Math.round(gpu.powerDraw)}W{gpu.powerLimit ? ` / ${Math.round(gpu.powerLimit)}W` : ''}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function StatusBadge({ status }: { status: ManagedModel['status'] }) {
  switch (status) {
    case 'running':
      return <Badge className="border-0 bg-emerald-600/90 text-white shadow-sm shadow-emerald-900/40">Running</Badge>
    case 'downloaded':
      return <Badge variant="secondary">Downloaded</Badge>
    case 'downloading':
      return <Badge className="border-0 bg-blue-600/90 text-white">Downloading</Badge>
    case 'stopped':
      return <Badge variant="outline">Stopped</Badge>
    case 'error':
      return <Badge variant="destructive">Error</Badge>
  }
}

function CapabilityBadges({ capabilities }: { capabilities?: ModelCapabilities }) {
  if (!capabilities) {
    return <Badge variant="outline" className="text-xs bg-zinc-600/20 text-zinc-400 border-zinc-500/30">
      <MessageSquare className="h-3 w-3 mr-1" />Chat Only
    </Badge>
  }

  return (
    <div className="flex gap-1.5 flex-wrap">
      {capabilities.agentic && (
        <Badge variant="outline" className="text-xs bg-purple-600/20 text-purple-400 border-purple-500/30">
          <Bot className="h-3 w-3 mr-1" />Agentic
        </Badge>
      )}
      {capabilities.toolCalling && (
        <Badge variant="outline" className="text-xs bg-blue-600/20 text-blue-400 border-blue-500/30">
          <Wrench className="h-3 w-3 mr-1" />Tool Calling
        </Badge>
      )}
      {capabilities.toolParser && (
        <Badge variant="outline" className="text-xs bg-amber-600/20 text-amber-400 border-amber-500/30">
          <Zap className="h-3 w-3 mr-1" />{capabilities.toolParser}
        </Badge>
      )}
      {!capabilities.toolCalling && !capabilities.agentic && (
        <Badge variant="outline" className="text-xs bg-zinc-600/20 text-zinc-400 border-zinc-500/30">
          <MessageSquare className="h-3 w-3 mr-1" />Chat Only
        </Badge>
      )}
    </div>
  )
}

function ModelSizeIndicator({ sizeBytes, gpuCount }: { sizeBytes?: number; gpuCount: number }) {
  if (!sizeBytes || sizeBytes === 0) return null

  const sizeGB = sizeBytes / (1024 * 1024 * 1024)
  const singleGpuVram = 24
  let fitLabel: string
  let fitColor: string

  if (sizeGB < singleGpuVram * 0.85) {
    fitLabel = 'Fits 1 GPU'
    fitColor = 'text-emerald-400'
  } else if (sizeGB < singleGpuVram * 2 * 0.85 && gpuCount >= 2) {
    fitLabel = 'Needs 2 GPUs'
    fitColor = 'text-amber-400'
  } else if (gpuCount < 2 && sizeGB >= singleGpuVram * 0.85) {
    fitLabel = 'Too large for 1 GPU'
    fitColor = 'text-red-400'
  } else {
    fitLabel = 'Too large'
    fitColor = 'text-red-400'
  }

  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-muted-foreground">{sizeGB.toFixed(1)} GB</span>
      <span className={fitColor}>{fitLabel}</span>
    </div>
  )
}

export default function ModelsPage() {
  const { stats, fetchStats } = useSystemStore()
  const { currentModel, setCurrentModel, fetchModels: fetchVllmModels } = useLlmStore()
  const [managedModels, setManagedModels] = useState<ManagedModel[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [actionInFlight, setActionInFlight] = useState<string | null>(null)
  const [downloadModelId, setDownloadModelId] = useState('')
  const [gpuSelections, setGpuSelections] = useState<Record<string, string>>({})

  const gpuCount = stats?.gpus.length ?? 0

  const fetchManagedModels = useCallback(async () => {
    const base = getGatewayUrl()
    try {
      setIsLoading(true)
      const res = await gatewayFetch(`${base}/models`)
      if (!res.ok) throw new Error(`Gateway returned ${res.status}`)
      const data = await res.json()
      setManagedModels(data.models || [])
      setError(null)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchStats()
    fetchManagedModels()
    fetchVllmModels()
    const interval = setInterval(() => {
      fetchStats()
      fetchManagedModels()
    }, 8000)
    return () => clearInterval(interval)
  }, [fetchStats, fetchManagedModels, fetchVllmModels])

  const apiAction = async (path: string, method: string = 'POST', body?: unknown) => {
    const base = getGatewayUrl()
    const res = await gatewayFetch(`${base}${path}`, {
      method,
      headers: body ? { 'Content-Type': 'application/json' } : {},
      body: body ? JSON.stringify(body) : undefined,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }))
      throw new Error(err.error || `Request failed: ${res.status}`)
    }
    return res.json()
  }

  const handleStart = async (modelId: string) => {
    setActionInFlight(modelId)
    try {
      const gpuSelection = gpuSelections[modelId] || 'auto'
      const body: { gpu?: number; tensorParallel?: number } = {}

      if (gpuSelection === 'both') {
        body.tensorParallel = 2
      } else if (gpuSelection === '0' || gpuSelection === '1') {
        body.gpu = parseInt(gpuSelection)
      }

      await apiAction(`/models/${encodeURIComponent(modelId)}/start`, 'POST', body)
      await fetchManagedModels()
    } catch (err) {
      setError(`Start failed: ${(err as Error).message}`)
    } finally {
      setActionInFlight(null)
    }
  }

  const handleStop = async (modelId: string) => {
    setActionInFlight(modelId)
    try {
      await apiAction(`/models/${encodeURIComponent(modelId)}/stop`, 'POST')
      await fetchManagedModels()
    } catch (err) {
      setError(`Stop failed: ${(err as Error).message}`)
    } finally {
      setActionInFlight(null)
    }
  }

  const handleDownload = async (modelId: string) => {
    if (!modelId.trim()) return
    setActionInFlight(modelId)
    try {
      await apiAction(`/models/${encodeURIComponent(modelId)}/download`, 'POST')
      setDownloadModelId('')
      await fetchManagedModels()
    } catch (err) {
      setError(`Download failed: ${(err as Error).message}`)
    } finally {
      setActionInFlight(null)
    }
  }

  const handleDelete = async (modelId: string) => {
    if (!confirm(`Delete model "${modelId}" from disk? This cannot be undone.`)) return
    setActionInFlight(modelId)
    try {
      await apiAction(`/models/${encodeURIComponent(modelId)}`, 'DELETE')
      await fetchManagedModels()
    } catch (err) {
      setError(`Delete failed: ${(err as Error).message}`)
    } finally {
      setActionInFlight(null)
    }
  }

  const runningModels = managedModels.filter((m) => m.status === 'running')
  const downloadedModels = managedModels.filter((m) => m.status === 'downloaded')
  const downloadingModels = managedModels.filter((m) => m.status === 'downloading')
  const errorModels = managedModels.filter((m) => m.status === 'error')

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Models</h1>
          <p className="text-muted-foreground mt-1">
            Manage LLM models, GPU allocation, and vLLM instances
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { fetchManagedModels(); fetchStats(); fetchVllmModels() }}
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Refresh
        </Button>
      </div>

      {/* GPU Overview */}
      {stats && stats.gpus.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {stats.gpus.map((gpu) => (
            <GPUCard key={gpu.id} gpu={gpu} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardContent className="py-6">
              <p className="text-sm text-muted-foreground">
                {stats === null ? 'Loading GPU data...' : 'No GPUs detected'}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-3 p-4 rounded-lg border border-amber-500/30 bg-amber-500/5">
          <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-amber-500">{error}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setError(null)} className="text-amber-500 hover:text-amber-400">
            Dismiss
          </Button>
        </div>
      )}

      {/* Download New Model */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Plus className="h-4 w-4" /> Download Model from HuggingFace
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="e.g. Qwen/Qwen2.5-Coder-32B-Instruct-AWQ"
              value={downloadModelId}
              onChange={(e) => setDownloadModelId(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleDownload(downloadModelId) }}
              className="flex-1 px-3 py-2 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <Button
              size="sm"
              onClick={() => handleDownload(downloadModelId)}
              disabled={!downloadModelId.trim() || actionInFlight === downloadModelId}
            >
              {actionInFlight === downloadModelId ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Download
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Downloading Models */}
      {downloadingModels.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Downloading</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {downloadingModels.map((model) => (
              <div key={model.id} className="space-y-2 p-4 border border-border rounded-lg bg-muted/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                    <span className="font-medium text-sm">{model.name}</span>
                    <StatusBadge status={model.status} />
                  </div>
                  <span className="text-sm text-muted-foreground">{model.downloadProgress ?? 0}%</span>
                </div>
                <Progress value={model.downloadProgress ?? 0} className="h-1.5" />
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Running Models */}
      <Card>
        <CardHeader>
          <CardTitle>Running Models</CardTitle>
        </CardHeader>
        <CardContent>
          {runningModels.length > 0 ? (
            <div className="space-y-4">
              {runningModels.map((model) => (
                <div
                  key={model.id}
                  className="flex items-center justify-between p-4 border border-border rounded-lg bg-muted/30"
                >
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 flex-wrap">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                      <span className="font-medium">{model.name}</span>
                      <StatusBadge status={model.status} />
                      {model.name === currentModel && (
                        <Badge variant="outline">Active</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground ml-6">
                      {model.gpu !== undefined && <span>GPU {model.gpu}</span>}
                      {model.port && <span>:{model.port}</span>}
                      {model.pid && <span>PID {model.pid}</span>}
                    </div>
                    <div className="ml-6">
                      <CapabilityBadges capabilities={model.capabilities} />
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {model.name !== currentModel && (
                      <Button variant="outline" size="sm" onClick={() => setCurrentModel(model.name)}>
                        Use for Chat
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStop(model.id)}
                      disabled={actionInFlight === model.id}
                      className="text-red-500 hover:text-red-400 hover:border-red-500/50"
                    >
                      {actionInFlight === model.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Square className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : isLoading ? (
            <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading model status...
            </div>
          ) : (
            <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
              <XCircle className="h-4 w-4" />
              No running models. Start one from the downloaded models below, or download a new one.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Downloaded Models (not currently running) */}
      {downloadedModels.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Downloaded Models</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {downloadedModels.map((model) => (
              <div
                key={model.id}
                className="flex items-center justify-between p-4 border border-border rounded-lg bg-muted/30"
              >
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 flex-wrap">
                    <span className="font-medium">{model.name}</span>
                    <StatusBadge status={model.status} />
                  </div>
                  <div className="ml-0 flex items-center gap-3">
                    <CapabilityBadges capabilities={model.capabilities} />
                    <ModelSizeIndicator sizeBytes={model.sizeBytes} gpuCount={gpuCount} />
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {gpuCount > 1 && (
                    <Select
                      value={gpuSelections[model.id] || 'auto'}
                      onValueChange={(v) =>
                        setGpuSelections((prev) => ({ ...prev, [model.id]: v }))
                      }
                    >
                      <SelectTrigger className="w-[140px] h-8 text-xs">
                        <SelectValue placeholder="GPU" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Auto</SelectItem>
                        <SelectItem value="0">GPU 0</SelectItem>
                        <SelectItem value="1">GPU 1</SelectItem>
                        <SelectItem value="both">Both (Tensor Parallel)</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleStart(model.id)}
                    disabled={actionInFlight === model.id}
                  >
                    {actionInFlight === model.id ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="h-4 w-4 mr-2" />
                    )}
                    Start
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(model.id)}
                    disabled={actionInFlight === model.id}
                    className="text-red-500 hover:text-red-400 hover:border-red-500/50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Error Models */}
      {errorModels.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-red-500">Failed</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {errorModels.map((model) => (
              <div key={model.id} className="p-3 border border-red-500/30 rounded-lg bg-red-500/5">
                <div className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-500" />
                  <span className="font-medium text-sm">{model.name}</span>
                </div>
                {model.error && (
                  <p className="text-xs text-muted-foreground mt-1 ml-6">{model.error}</p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* LLM Services from system stats */}
      {stats && stats.llms.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>LLM Service Health</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.llms.map((llm) => (
                <div
                  key={llm.port}
                  className="flex items-center justify-between p-3 border border-border rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <div
                      className={`h-2 w-2 rounded-full shrink-0 ${
                        llm.status === 'online'
                          ? 'bg-emerald-500'
                          : llm.status === 'loading'
                          ? 'bg-amber-400 animate-pulse'
                          : 'bg-rose-500'
                      }`}
                    />
                    <span className="text-sm font-medium">{llm.model}</span>
                    <span className="text-xs text-muted-foreground">:{llm.port}</span>
                  </div>
                  <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                    <span>{llm.tokensGenerated} tokens</span>
                    <span>{llm.averageLatency}ms avg</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
